import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate, Link } from "react-router-dom";
import config from '../../config';

function ProjectDetails() {
    const { id } = useParams(); // Get the project ID from the URL
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeLanguage, setActiveLanguage] = useState('en');
    const navigate = useNavigate();

    const fetchProjectDetails = async () => {
        try {
            const response = await axios.get(`${config.BASE_URL}/api/admin/get-project-admin/${id}`, { withCredentials: true });
            setProject(response.data);
        } catch (err) {
            setError('Failed to fetch project details');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchProjectDetails();
    }, [id]);

    const handleDelete = async () => {
        try {
            await axios.delete(`${config.BASE_URL}/api/admin/delete-project/${id}`, { withCredentials: true });
            // Navigate back to the project list or show a success message
            navigate('/admin/projects');
        } catch (err) {
            setError('Failed to delete the project');
        }
    };

    if (loading) return <p>Loading...</p>;
    if (error) return <p>{error}</p>;

    return (
        <div className="dashboard project_detail_wrap">
            <div className="container">
                <h1 className="dash_title">Dashboard</h1>
                <div className="back_btn_wrap">
                    <button className="back-btn" onClick={() => navigate(-1)}>Go Back</button>
                </div>
                <div className="main-section">
                    <div className="projects">
                        <div className="projects-inner">
                            {/* Language Tabs */}
                            <div className="language-tabs" style={{ marginBottom: '20px' }}>
                                <button
                                    className={`language-tab ${activeLanguage === 'en' ? 'active' : ''}`}
                                    onClick={() => setActiveLanguage('en')}
                                    style={{
                                        padding: '10px 20px',
                                        marginRight: '10px',
                                        border: '1px solid #ddd',
                                        background: activeLanguage === 'en' ? '#007bff' : '#fff',
                                        color: activeLanguage === 'en' ? '#fff' : '#333',
                                        cursor: 'pointer',
                                        borderRadius: '4px'
                                    }}
                                >
                                    English
                                </button>
                                <button
                                    className={`language-tab ${activeLanguage === 'es' ? 'active' : ''}`}
                                    onClick={() => setActiveLanguage('es')}
                                    style={{
                                        padding: '10px 20px',
                                        border: '1px solid #ddd',
                                        background: activeLanguage === 'es' ? '#007bff' : '#fff',
                                        color: activeLanguage === 'es' ? '#fff' : '#333',
                                        cursor: 'pointer',
                                        borderRadius: '4px'
                                    }}
                                >
                                    Spanish
                                </button>
                            </div>

                            {/* English Title */}
                            {activeLanguage === 'en' && (
                                <div className="detail_item">
                                    <h2>Title (English)</h2>
                                    <div className="detail_item_inner">
                                        <p>{project.title}</p>
                                    </div>
                                </div>
                            )}

                            {/* Spanish Title */}
                            {activeLanguage === 'es' && (
                                <div className="detail_item">
                                    <h2>Title (Spanish)</h2>
                                    <div className="detail_item_inner">
                                        <p>{project.spanishData?.title || 'No Spanish title available'}</p>
                                    </div>
                                </div>
                            )}

                            {/* English Categories */}
                            {activeLanguage === 'en' && Array.isArray(project?.category) && project.category.length > 0 && (
                                <div className="detail_item">
                                    <h2>Categories (English)</h2>
                                    <div className="detail_item_inner">
                                        <p>
                                            {project.category.join(', ')}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Spanish Categories */}
                            {activeLanguage === 'es' && Array.isArray(project?.spanishData?.category) && project.spanishData.category.length > 0 && (
                                <div className="detail_item">
                                    <h2>Categories (Spanish)</h2>
                                    <div className="detail_item_inner">
                                        <p>
                                            {project.spanishData.category.join(', ')}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Show message when no categories available */}
                            {activeLanguage === 'en' && (!Array.isArray(project?.category) || project.category.length === 0) && (
                                <div className="detail_item">
                                    <h2>Categories (English)</h2>
                                    <div className="detail_item_inner">
                                        <p>No English categories available.</p>
                                    </div>
                                </div>
                            )}

                            {activeLanguage === 'es' && (!Array.isArray(project?.spanishData?.category) || project.spanishData.category.length === 0) && (
                                <div className="detail_item">
                                    <h2>Categories (Spanish)</h2>
                                    <div className="detail_item_inner">
                                        <p>No  categories available.</p>
                                    </div>
                                </div>
                            )}

                            {/* English Fields */}
                            {activeLanguage === 'en' && (
                                <>
                                    <div className="detail_item">
                                        <h2>Ano (English)</h2>
                                        <div className="detail_item_inner">
                                            <p>{project.ano}</p>
                                        </div>
                                    </div>

                                    <div className="detail_item">
                                        <h2>Cliente (English)</h2>
                                        <div className="detail_item_inner">
                                            <p>{project.cliente}</p>
                                        </div>
                                    </div>
                                    <div className="detail_item">
                                        <h2>Location (English)</h2>
                                        <div className="detail_item_inner">
                                            <p>{project?.location}</p>
                                        </div>
                                    </div>

                                    <div className="detail_item">
                                        <h2>Lighting Designer (English)</h2>
                                        <div className="detail_item_inner">
                                            <p>{project.lightingDesigner}</p>
                                        </div>
                                    </div>

                                    <div className="detail_item">
                                        <h2>Interior Designer (English)</h2>
                                        <div className="detail_item_inner">
                                            <p>{project.interiorDesigner}</p>
                                        </div>
                                    </div>

                                    <div className="detail_item">
                                        <h2>Photography (English)</h2>
                                        <div className="detail_item_inner">
                                            <p>{project.photography}</p>
                                        </div>
                                    </div>

                                    <div className="detail_item">
                                        <h2>Partners (English)</h2>
                                        <div className="detail_item_inner">
                                            <p>{project.partners}</p>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Spanish Fields */}
                            {activeLanguage === 'es' && (
                                <>
                                    <div className="detail_item">
                                        <h2>Ano (Spanish)</h2>
                                        <div className="detail_item_inner">
                                            <p>{project.spanishData?.ano || 'No Spanish year available'}</p>
                                        </div>
                                    </div>

                                    <div className="detail_item">
                                        <h2>Cliente (Spanish)</h2>
                                        <div className="detail_item_inner">
                                            <p>{project.spanishData?.cliente || 'No Spanish client available'}</p>
                                        </div>
                                    </div>
                                    
                                    <div className="detail_item">
                                        <h2>Location (Spanish)</h2>
                                        <div className="detail_item_inner">
                                            <p>{project.spanishData?.location || 'No Spanish client available'}</p>
                                        </div>
                                    </div>

                                    <div className="detail_item">
                                        <h2>Lighting Designer (Spanish)</h2>
                                        <div className="detail_item_inner">
                                            <p>{project.spanishData?.lightingDesigner || 'No Spanish lighting designer available'}</p>
                                        </div>
                                    </div>

                                    <div className="detail_item">
                                        <h2>Interior Designer (Spanish)</h2>
                                        <div className="detail_item_inner">
                                            <p>{project.spanishData?.interiorDesigner || 'No Spanish interior designer available'}</p>
                                        </div>
                                    </div>

                                    <div className="detail_item">
                                        <h2>Photography (Spanish)</h2>
                                        <div className="detail_item_inner">
                                            <p>{project.spanishData?.photography || 'No Spanish photography available'}</p>
                                        </div>
                                    </div>

                                    <div className="detail_item">
                                        <h2>Partners (Spanish)</h2>
                                        <div className="detail_item_inner">
                                            <p>{project.spanishData?.partners || 'No Spanish partners available'}</p>
                                        </div>
                                    </div>
                                </>
                            )}

                            <div className="detail_item">
                                <div className="detail_banner_wrap">
                                    <div className="detail_img_grid">
                                        {project.featuredImage ? (
                                            <div className="detail_img_item">
                                                <h2>Featured Image</h2>
                                                <div className="banner_img_wrap">
                                                    <img src={`${config.BASE_URL}${project.featuredImage}`} alt="Featured" />
                                                </div>
                                            </div>
                                        ) : (
                                            <p>No image available.</p>
                                        )}

                                    </div>
                                </div>
                            </div>

                            {/* English Center Content */}
                            {activeLanguage === 'en' && (
                                <div className="add_field textareaField repeaterFields">
                                    <h2><b>Center Content (English)</b></h2>
                                    {project?.centerContent && project.centerContent.length > 0 ? (
                                        project.centerContent.map((field, index) => (
                                            <div key={index} className="singleReapterFieldContent update displayContent">
                                                <div className="detail_item">
                                                    <h2>Description</h2>
                                                    <div className="detail_item_inner">
                                                        <p>{field.description}</p>
                                                    </div>
                                                </div>

                                                <div className="detail_item">
                                                    <h2>Image</h2>
                                                    {field?.image ? (
                                                        <div className="detail_img_item">
                                                            <div className="banner_img_wrap">
                                                                <img src={`${config.BASE_URL}${field?.image}`} alt={`Center Content ${index}`} />
                                                            </div>
                                                        </div>
                                                    ) : <p>No Image added</p>}
                                                </div>

                                                <div className="detail_item">
                                                    <h2>Is Full Width?</h2>
                                                    <div className="detail_item_inner">
                                                        <p>{field?.isFullWidth ? "Yes" : "No"}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p>No English center content available.</p>
                                    )}
                                </div>
                            )}

                            {/* Spanish Center Content */}
                            {activeLanguage === 'es' && (
                                <div className="add_field textareaField repeaterFields">
                                    <h2><b>Center Content (Spanish)</b></h2>
                                    {project?.spanishData?.centerContent && project.spanishData.centerContent.length > 0 ? (
                                        project.spanishData.centerContent.map((field, index) => (
                                            <div key={index} className="singleReapterFieldContent update displayContent">
                                                <div className="detail_item">
                                                    <h2>Description</h2>
                                                    <div className="detail_item_inner">
                                                        <p>{field.description}</p>
                                                    </div>
                                                </div>

                                                <div className="detail_item">
                                                    <h2>Image</h2>
                                                    {field?.image ? (
                                                        <div className="detail_img_item">
                                                            <div className="banner_img_wrap">
                                                                <img src={`${config.BASE_URL}${field?.image}`} alt={`Contenido Central ${index}`} />
                                                            </div>
                                                        </div>
                                                    ) : <p>No Image added</p>}
                                                </div>

                                                <div className="detail_item">
                                                    <h2>Is Full Width?</h2>
                                                    <div className="detail_item_inner">
                                                        <p>{field?.isFullWidth ? "Sí" : "No"}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p>No English center content available.</p>
                                    )}
                                </div>
                            )}

                            <div className="detail_item">
                                <h2>Gallery Images</h2>
                                <div className="detail_item_inner gallery_inner">
                                    <div className="detail_img_grid">
                                        {project.galleryImages && project.galleryImages.length > 0 ? (
                                            project.galleryImages.map((image, index) => (
                                                <div className="detail_img_item" key={index}>
                                                    <img src={`${config.BASE_URL}${image}`} alt={`Project image ${index}`} />
                                                </div>
                                            ))
                                        ) : (
                                            <p>No images available.</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="detail_item">
                                <h2>Priority</h2>
                                <div className="detail_item_inner">
                                    <p>{project?.priorityNumber ? project?.priorityNumber : "Not Set"}</p>
                                </div>
                            </div>
                            <div className="detail_item">
                                <h2>Is Featured</h2>
                                <div className="detail_item_inner">
                                    <p>{project.isFeatured ? "Yes" : "No"}</p>
                                </div>
                            </div>

                            <div className="detail_item">
                                <h2>Is HomePage Featured</h2>
                                <div className="detail_item_inner">
                                    <p>{project.isHomePageFeatured ? "Yes" : "No"}</p>
                                </div>
                            </div>

                            <div className="detail_project_btn_wrap">
                                <div className="detail_edit_del">
                                    <div className="btn_item">
                                        <button className="detail_btns" onClick={handleDelete}>Delete</button>
                                    </div>
                                    <div className="btn_item">
                                        <Link to={`/admin/update-project/${project._id}`} className="update_detail detail_btns">Edit</Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ProjectDetails;
